// Top-level router. One SPA serves four role surfaces:
//
//   /                            marketing/landing (public)
//   /login, /register            auth (public)
//   /customer/*                  diaspora portal       (role=customer)
//   /recipient/*                 HT recipient PWA      (role=recipient)
//   /merchant/*                  vendor console        (role=merchant)
//   /admin/*                     platform ops          (role=admin / super_admin)
//
// Every sidebar menu item has a route. Real pages where the underlying
// data exists; ShellPlaceholder pages where the feature ships in a
// later phase. Nothing 404s.

import { Routes, Route, Navigate, Link } from 'react-router-dom';
import { useAuth } from './contexts/AuthContext';
import { RequireAuth } from './components/shared/RequireAuth';
import { Landing } from './pages/Landing';
import { RecipientHome } from './pages/RecipientHome';
import { CustomerDashboard } from './pages/CustomerDashboard';
import { MerchantDashboard } from './pages/MerchantDashboard';
import { AdminDashboard } from './pages/AdminDashboard';
import { ComingSoon } from './pages/ComingSoon';
import { ShellPlaceholder } from './pages/ShellPlaceholder';
import { SettingsPage } from './pages/SettingsPage';
import { Login } from './pages/auth/Login';
import { Register } from './pages/auth/Register';
import { ForgotPassword } from './pages/auth/ForgotPassword';
import { ResetPassword } from './pages/auth/ResetPassword';

// Customer
import { AirtimePage } from './pages/customer/AirtimePage';
import { airtimeEnabled } from './lib/featureFlags';
import { NewRequestPage } from './pages/customer/NewRequestPage';
import { CustomerCarePackagesPage } from './pages/customer/CarePackagesPage';
import { CustomerRequestsPage } from './pages/customer/RequestsPage';
import { CustomerRequestDetailPage } from './pages/customer/RequestDetailPage';
import { CustomerRecipientsPage } from './pages/customer/RecipientsPage';
import { CustomerRecipientDetailPage } from './pages/customer/RecipientDetailPage';

// Merchant
import { InventoryPage } from './pages/merchant/InventoryPage';
import { MerchantQuoteRequestsPage } from './pages/merchant/QuoteRequestsPage';
import { MerchantQuoteBuilderPage } from './pages/merchant/QuoteBuilderPage';
import { MerchantProfilePage } from './pages/merchant/ProfilePage';
import { MerchantOrdersPage } from './pages/merchant/OrdersPage';

// Admin
import { AdminRequestsPage } from './pages/admin/RequestsPage';
import { AdminMerchantsPage } from './pages/admin/MerchantsPage';
import { AdminUsersPage } from './pages/admin/UsersPage';
import { AdminCarePackagesPage } from './pages/admin/CarePackagesPage';
import { AdminCatalogPage } from './pages/admin/CatalogPage';
import { AdminFxPage } from './pages/admin/FxPage';
import { AdminFeesPage } from './pages/admin/FeesPage';
import { AdminSettingsPage } from './pages/admin/SettingsPage';
import { BasketPlannerPage } from './pages/shared/BasketPlannerPage';
import { RecipientClaim } from './pages/RecipientClaim';
import { HelpPage } from './pages/HelpPage';
import { AdminDisputesPage } from './pages/admin/DisputesPage';

// Shared
import { MerchantDetailPage } from './pages/MerchantDetailPage';
import { CarePackagesPage, PackageDetailPage } from './pages/CarePackages';
import { ClaimStorePage } from './pages/ClaimStore';
import { CustomerHistoryPage } from './pages/customer/HistoryPage';
import { RecipientHistoryPage } from './pages/recipient/HistoryPage';
import { MerchantPayoutsPage } from './pages/merchant/PayoutsPage';
import { AdminPayoutsPage } from './pages/admin/PayoutsPage';

// Nav configs (used by ShellPlaceholder pages so the sidebar still renders).
import { CUSTOMER_NAV, ADMIN_NAV } from './pages/_navs';

function NotFound() {
  return (
    <div className="min-h-screen flex flex-col items-center justify-center text-slate-700">
      <p className="text-[11px] uppercase tracking-[0.2em] text-slate-400">404</p>
      <p className="mt-2 text-lg">This page doesn't exist.</p>
      <Link to="/" className="mt-4 text-brand-600 hover:underline text-[13px]">Back to VoyeLakay</Link>
    </div>
  );
}

export default function App() {
  const { loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-[12px] text-slate-400">
        Loading…
      </div>
    );
  }

  const isAdminHost = typeof window !== 'undefined' && window.location.hostname.startsWith('admin.');

  return (
    <Routes>
      <Route path="/" element={isAdminHost ? <Navigate to="/admin" replace /> : <Landing />} />

      {/* Care packages — public marketing + auth-gated send flow */}
      <Route path="/packages"       element={<CarePackagesPage />} />
      <Route path="/packages/:slug" element={<PackageDetailPage />} />

      {/* Claim your store — public business directory (OSM) */}
      <Route path="/claim" element={<ClaimStorePage />} />

      {/* Auth — public */}
      <Route path="/login"    element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password"  element={<ResetPassword />} />
      <Route path="/r/:token" element={<RecipientClaim />} />

      {/* Legal stubs */}
      <Route path="/privacy" element={<ComingSoon title="Privacy notice"  body="Our privacy notice is being drafted with counsel. Email hello@lakaycart.com for the current data-handling summary." />} />
      <Route path="/terms"   element={<ComingSoon title="Terms of service" body="Terms are being drafted with counsel. Email hello@lakaycart.com for the current draft." />} />

      {/* ─── Customer ─────────────────────────────────────── */}
      <Route path="/customer"                     element={<RequireAuth allow={['customer']}><CustomerDashboard /></RequireAuth>} />
      <Route path="/customer/requests"            element={<RequireAuth allow={['customer']}><CustomerRequestsPage /></RequireAuth>} />
      <Route path="/customer/requests/new"        element={<RequireAuth allow={['customer']}><NewRequestPage /></RequireAuth>} />
      {/* Airtime (Voye send-product) — additive, flag-gated. Absent in prod
          until VITE_AIRTIME_ENABLED=true, so no live surface changes by default. */}
      {airtimeEnabled() && (
        <Route path="/customer/airtime"           element={<RequireAuth allow={['customer']}><AirtimePage /></RequireAuth>} />
      )}
      <Route path="/customer/packages"            element={<RequireAuth allow={['customer']}><CustomerCarePackagesPage /></RequireAuth>} />
      <Route path="/customer/plan"                 element={<RequireAuth allow={['customer']}><BasketPlannerPage /></RequireAuth>} />
      <Route path="/customer/help"                 element={<RequireAuth allow={['customer']}><HelpPage /></RequireAuth>} />
      <Route path="/customer/requests/:id"        element={<RequireAuth allow={['customer']}><CustomerRequestDetailPage /></RequireAuth>} />
      <Route path="/customer/recipients"          element={<RequireAuth allow={['customer']}><CustomerRecipientsPage /></RequireAuth>} />
      <Route path="/customer/recipients/:id"      element={<RequireAuth allow={['customer']}><CustomerRecipientDetailPage /></RequireAuth>} />
      <Route path="/customer/merchants/:id"       element={<RequireAuth allow={['customer']}><MerchantDetailPage /></RequireAuth>} />
      <Route path="/customer/settings"            element={<RequireAuth allow={['customer']}><SettingsPage /></RequireAuth>} />
      <Route path="/customer/payment-methods"     element={<RequireAuth allow={['customer']}><ShellPlaceholder
        nav={CUSTOMER_NAV} activeId="methods" phase="Phase 3 — Stripe checkout"
        title="Payment methods"
        subtitle="Cards you've saved for paying merchant quotes."
        body="Stripe Elements + saved cards land with the payment integration in Phase 3. Until then, the cart-builder submits a request but no card is collected — that step kicks in once a merchant returns a quote."
        bullets={[
          'Add / remove cards via Stripe Elements',
          'Set a default for recurring support',
          'Receipts emailed via Resend on each charge',
        ]}
        backLink={{ to: '/customer', label: 'Back to dashboard' }}
      /></RequireAuth>} />
      <Route path="/customer/history"             element={<RequireAuth allow={['customer']}><CustomerHistoryPage /></RequireAuth>} />
      <Route path="/customer/recurring"           element={<RequireAuth allow={['customer']}><ShellPlaceholder
        nav={CUSTOMER_NAV} activeId="recurring" phase="Phase 5 — Recurring plans"
        title="Recurring support"
        subtitle="Send $X every week/two-weeks/month, automatically."
        body="Schedule recurring family support — pick a recipient + a budget + a cadence and we'll auto-create requests on your behalf. Runs after the payment + scheduler wiring in Phase 5."
        bullets={[
          'Per-recipient budget per cycle',
          'Weekly / biweekly / monthly cadence',
          'Pause / cancel anytime; merchant always sees the same flow',
        ]}
      /></RequireAuth>} />

      {/* ─── Recipient (PWA) ──────────────────────────────── */}
      <Route path="/recipient"                  element={<RequireAuth allow={['recipient']}><RecipientHome /></RequireAuth>} />
      <Route path="/recipient/merchants/:id"    element={<RequireAuth allow={['recipient']}><MerchantDetailPage /></RequireAuth>} />
      <Route path="/recipient/request/new"      element={<RequireAuth allow={['recipient']}><NewRequestPage /></RequireAuth>} />
      <Route path="/recipient/plan"             element={<RequireAuth allow={['recipient']}><BasketPlannerPage /></RequireAuth>} />
      <Route path="/recipient/help"             element={<RequireAuth allow={['recipient']}><HelpPage /></RequireAuth>} />
      <Route path="/recipient/history"          element={<RequireAuth allow={['recipient']}><RecipientHistoryPage /></RequireAuth>} />
      <Route path="/recipient/settings"         element={<RequireAuth allow={['recipient']}><SettingsPage /></RequireAuth>} />

      {/* ─── Merchant ─────────────────────────────────────── */}
      <Route path="/merchant"                   element={<RequireAuth allow={['merchant']}><MerchantDashboard /></RequireAuth>} />
      <Route path="/merchant/inventory"         element={<RequireAuth allow={['merchant']}><InventoryPage /></RequireAuth>} />
      <Route path="/merchant/settings"          element={<RequireAuth allow={['merchant']}><SettingsPage /></RequireAuth>} />
      <Route path="/merchant/settings/profile"  element={<RequireAuth allow={['merchant']}><MerchantProfilePage /></RequireAuth>} />
      <Route path="/merchant/quote-requests" element={<RequireAuth allow={['merchant']}><MerchantQuoteRequestsPage /></RequireAuth>} />
      <Route path="/merchant/quote-requests/:id" element={<RequireAuth allow={['merchant']}><MerchantQuoteBuilderPage /></RequireAuth>} />
      <Route path="/merchant/orders"         element={<RequireAuth allow={['merchant']}><MerchantOrdersPage /></RequireAuth>} />
      <Route path="/merchant/payouts"           element={<RequireAuth allow={['merchant']}><MerchantPayoutsPage /></RequireAuth>} />
      <Route path="/merchant/help"              element={<RequireAuth allow={['merchant']}><HelpPage /></RequireAuth>} />

      {/* ─── Admin ────────────────────────────────────────── */}
      <Route path="/admin"                element={<RequireAuth allow={['admin', 'super_admin']}><AdminDashboard /></RequireAuth>} />
      <Route path="/admin/settings"       element={<RequireAuth allow={['admin', 'super_admin']}><SettingsPage /></RequireAuth>} />
      <Route path="/admin/users"     element={<RequireAuth allow={['admin', 'super_admin']}><AdminUsersPage /></RequireAuth>} />
      <Route path="/admin/merchants"     element={<RequireAuth allow={['admin', 'super_admin']}><AdminMerchantsPage /></RequireAuth>} />
      <Route path="/admin/merchants/:id" element={<RequireAuth allow={['admin', 'super_admin']}><MerchantDetailPage /></RequireAuth>} />
      <Route path="/admin/care-packages" element={<RequireAuth allow={['admin', 'super_admin']}><AdminCarePackagesPage /></RequireAuth>} />
      <Route path="/admin/catalog"       element={<RequireAuth allow={['admin', 'super_admin']}><AdminCatalogPage /></RequireAuth>} />
      <Route path="/admin/requests"  element={<RequireAuth allow={['admin', 'super_admin']}><AdminRequestsPage /></RequireAuth>} />
      <Route path="/admin/payouts"        element={<RequireAuth allow={['admin', 'super_admin']}><AdminPayoutsPage /></RequireAuth>} />
      <Route path="/admin/disputes"       element={<RequireAuth allow={['admin', 'super_admin']}><AdminDisputesPage /></RequireAuth>} />
      <Route path="/admin/fx"             element={<RequireAuth allow={['admin', 'super_admin']}><AdminFxPage /></RequireAuth>} />
      <Route path="/admin/fees"           element={<RequireAuth allow={['admin', 'super_admin']}><AdminFeesPage /></RequireAuth>} />
      {/* Runtime platform config (fees, flags, limits). Distinct from
          /admin/settings, which is the admin's own ACCOUNT settings. */}
      <Route path="/admin/platform"       element={<RequireAuth allow={['admin', 'super_admin']}><AdminSettingsPage /></RequireAuth>} />
      <Route path="/admin/help"           element={<RequireAuth allow={['admin', 'super_admin']}><HelpPage /></RequireAuth>} />
      <Route path="/admin/audit-logs"     element={<RequireAuth allow={['admin', 'super_admin']}><ShellPlaceholder
        nav={ADMIN_NAV} activeId="audit" phase="Phase 3 — Audit"
        title="Audit log"
        subtitle="Every state change, who did it, what changed."
        body="Append-only ledger. Filter by entity_type + entity_id + actor + date. Investigations and compliance reviews live here."
      /></RequireAuth>} />
      <Route path="/admin/docs"           element={<RequireAuth allow={['admin', 'super_admin']}><ShellPlaceholder
        nav={ADMIN_NAV} activeId="docs" phase="Phase 4 — In-app docs"
        title="Operator docs"
        body="Embedded operator runbooks pulled from the docs/ folder of the repo. Until then, see github.com/michelevens/lakaycart-app/blob/main/docs/USER_FLOWS.md"
      /></RequireAuth>} />

      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}
